import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getDashboard,
  getEvent,
  getEvents,
  getWidgets,
  postDataTables,
  putActionResolve,
  searchEvents
} from "services/api";
import { normalizeDataEvent, normalizeDataTable, updateResolve } from './operators';
import { setResolution } from '../userDetail/userDetailSlice';

export interface DashboardState {
  eventsList: any;
  event: {
    status: null | 'idle' | 'loading' | 'failed';
    data: any;
  };
  filterableValues: any;
  pagination: {
    total: number;
  };
  status: null | 'idle' | 'loading' | 'failed';
  session: {
    success: boolean;
    errorCode: number;
  };
  widgets: {
    status: null | 'idle' | 'loading' | 'failed';
    data: any;
  };
  deviceSelected: object;
  resolved: {
    status: null | 'idle' | 'loading' | 'failed';
  };
  dashboards: null | any;
}

const initialState: DashboardState = {
  eventsList: [],
  event: {
    status: null,
    data: {},
  },
  filterableValues: {},
  pagination: {
    total: 0,
  },
  status: null,
  session: {
    success: false,
    errorCode: 0,
  },
  widgets: {
    status: null,
    data: {},
  },
  deviceSelected: {},
  resolved: {
    status: null,
  },
  dashboards: null,
};

export const getDashboardAsync: any | null = createAsyncThunk(
  'dashboard/getDashboard',
  async (params: any) => {
    const response = await getDashboard(params);
    return response?.data;
  },
);

export const postDataActionsAsync: any | null = createAsyncThunk(
  'dashboard/postDataTables',
  async (data: any) => {
    const response = await postDataTables(data.filters, data.pathname);
    return response;
  },
);

export const getEventsAsync: any | null = createAsyncThunk(
  'dashboard/getEvents',
  async (data: any) => {
    const response = await getEvents(data);
    return response?.data;
  },
);

// Búsqueda paginada con filtros. Recibe el shape que envía TableComponent
// ({ init, size, page, ...filtros }); `page` es sólo de UI y se descarta.
export const searchEventsAsync: any | null = createAsyncThunk(
  'dashboard/searchEvents',
  async (data: any) => {
    const { page, ...body } = data;
    const response = await searchEvents(body);
    return response?.data;
  },
);

export const getEventAsync: any | null = createAsyncThunk(
  'dashboard/getEvent',
  async (data: any) => {
    const response = await getEvent(data);
    return response;
  },
);

export const getAndSetEventAsync: any | null = createAsyncThunk(
  'dashboard/getEvent',
  async (data: any, { dispatch }) => {
    const response = await getEvent(data);

    dispatch(
      setResolution({
        patientId: response?.data.item.patientId,
        measureRawId: response?.data.item.measureRawId,
        applicantName: response?.data.item.resolution.applicantName,
        resolutionTimestamp: response?.data.item.resolution.timestamp,
        resolutionId: response?.data.item.resolution.resolution.id,
        deviceActionId: response?.data.item.eventId,
        rawActionType: response?.data.item.eventType,
        rawEventState: response?.data.item.eventState,
        reference: response?.data.item.reference,
        resolved: !!response?.data.item.resolution?.resolution?.id,
        date: response?.data.item.eventTimestamp,
        contextKey: data?.contextKey,
      }),
    );

    return response;
  },
);

export const getWidgetsAsync = createAsyncThunk('dashboard/getWidgets', async () => {
  const response = await getWidgets();
  return response;
});

export const putActionResolveAsync = createAsyncThunk('dashboard/putActionResolve', async (data) => {
  const response = await putActionResolve(data);
  return response;
});

export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setDeviceSelected: (state, action: PayloadAction<object>) => {
      state.deviceSelected = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(postDataActionsAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(postDataActionsAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.session = { success: action.payload?.data.success, errorCode: action.payload?.data?.errorCode };
        state.eventsList = normalizeDataTable(action.payload);
        state.filterableValues = action.payload?.data?.item?.filterableValues;
        state.pagination.total = action.payload?.data?.item?.total;
      })
      .addCase(postDataActionsAsync.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(getEventsAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getEventsAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.session = { success: action.payload?.data?.success, errorCode: action.payload?.data?.errorCode };
        state.eventsList = normalizeDataTable(action.payload);
        state.filterableValues = action.payload?.item?.filterableValues;
        state.pagination.total = action.payload?.item?.total;
      })
      .addCase(getEventsAsync.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(searchEventsAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(searchEventsAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.session = { success: action.payload?.success, errorCode: action.payload?.errorCode };
        state.eventsList = normalizeDataTable(action.payload);
        // total + facetas sólo vienen en la primera página (init==0); en paginación se conservan.
        if (!action.meta?.arg?.init) {
          state.filterableValues = action.payload?.item?.filterableValues;
          state.pagination.total = action.payload?.item?.total;
        }
      })
      .addCase(searchEventsAsync.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(getEventAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getEventAsync.fulfilled, (state, action) => {
        state.event.status = 'idle';
        state.event.data = normalizeDataEvent(action.payload);
      })
      .addCase(getEventAsync.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(getWidgetsAsync.pending, (state) => {
        state.widgets.status = 'loading';
      })
      .addCase(getWidgetsAsync.fulfilled, (state, action) => {
        state.widgets.status = 'idle';
        state.widgets.data = action.payload?.data;
      })
      .addCase(getWidgetsAsync.rejected, (state) => {
        state.widgets.status = 'failed';
      })
      .addCase(putActionResolveAsync.pending, (state) => {
        state.resolved.status = 'loading';
      })
      .addCase(putActionResolveAsync.fulfilled, (state, action) => {
        state.resolved.status = 'idle';
        state.eventsList = updateResolve(action.meta, state.eventsList);
      })
      .addCase(putActionResolveAsync.rejected, (state) => {
        state.resolved.status = 'failed';
      })
      .addCase(getDashboardAsync.fulfilled, (state, action) => {
        state.dashboards = action.payload?.item?.dashboards;
      });
  },
});

export const { setDeviceSelected } = dashboardSlice.actions;

export default dashboardSlice.reducer;
